import { BackupPolicyType, ResourceType } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useSupportedResourceTypes } from '@/data/inventory/useSupportedResourceTypes';

export const useBackupPolicyAvailableTypes = (): Record<
  BackupPolicyType,
  ResourceType[]
> => {
  const {
    EC2HighFrequencyBackupEnabled,
    RdsPitrEnabled,
    SapHanaPitrSupport,
    AwsFsxSupport,
    AzureSqlVirtualMachineSupport,
  } = useFeatureFlags();
  const allTypes = useSupportedResourceTypes();
  return useMemo(() => {
    const HighFrequencySupportedResourceTypes: ResourceType[] = [
      ...(EC2HighFrequencyBackupEnabled ? [ResourceType.AwsEc2] : []),
      ...[
        ResourceType.AwsDynamoDb,
        ResourceType.AwsS3,
        ResourceType.GcpCloudStorageBucket,
      ],
    ];

    const pitrPolicySupportedResourceTypes: ResourceType[] = [
      ...(AzureSqlVirtualMachineSupport
        ? [ResourceType.AzureSqlVirtualMachine]
        : []),
      ...(SapHanaPitrSupport ? [ResourceType.AzureSapHanaVm] : []),
    ];

    const nativePitrPolicySupportedResourceTypes: ResourceType[] = [
      ...(RdsPitrEnabled ? [ResourceType.AwsRds] : []),
    ];

    const nativeStandardPolicySupportedResourceTypes: ResourceType[] = [
      ...(AwsFsxSupport ? [ResourceType.AwsFsx, ResourceType.AwsEfs] : []),
    ];

    const standardPolicySupportedResourceTypes: ResourceType[] = [
      ...allTypes.filter(
        (type) => !nativeStandardPolicySupportedResourceTypes.includes(type)
      ),
    ];

    const policySupportedResourceTypes: Record<
      BackupPolicyType,
      ResourceType[]
    > = {
      [BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY]:
        HighFrequencySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_PITR]:
        pitrPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR]:
        nativePitrPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD]:
        standardPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_NATIVE_STANDARD]:
        nativeStandardPolicySupportedResourceTypes,
      [BackupPolicyType.BACKUP_POLICY_TYPE_UNSPECIFIED]: [],
    };

    return policySupportedResourceTypes;
  }, [
    allTypes,
    EC2HighFrequencyBackupEnabled,
    AzureSqlVirtualMachineSupport,
    RdsPitrEnabled,
    SapHanaPitrSupport,
    AwsFsxSupport,
  ]);
};
